/**
 * The address somebody typed at the email step, kept until we know who actually signed in.
 *
 * The email step used to be advisory in a way nobody could see. The address it collects
 * picks the provider, and on the Entra path it rides along as `login_hint`; then the
 * identity provider answers from its own cookie, and the account that comes back is
 * whoever used this browser last. Typing a colleague's address signed you in as yourself,
 * with nothing on screen saying so. On the MIKE Cloud path there is no parameter that can
 * change that: measured 2026-08-26, `/authorize` accepts `prompt` and `login_hint` and
 * forwards neither to where the credential check happens. So the address has to be
 * remembered on our side and checked against the session afterwards, which is the one
 * place both providers can be held to it.
 *
 * `sessionStorage`, for two reasons. The address has to survive a full page load and a
 * round trip off our origin, so a module variable is no good; and it must not outlive the
 * tab, because a tab that has typed nothing has asked for nobody, and a stale address from
 * yesterday would accuse a perfectly ordinary sign-in of being the wrong one.
 *
 * Cleared as soon as the question is answered: by `AuthGate` when the session matches, and
 * by the mismatch screen when the person decides which account to keep.
 */

const KEY = 'mikeai.requested-account';

/** `sessionStorage`, or null where the browser refuses it or there is no window at all. */
function defaultStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // Private modes and blocked-storage settings throw on access rather than returning
    // null. Losing the check degrades to today's behaviour, which is the direction that
    // keeps somebody signed in rather than stranding them.
    return null;
  }
}

export function rememberRequestedAccount(
  email: string | null,
  store: Storage | null = defaultStore(),
): void {
  const value = email?.trim();
  if (!value) return;
  try {
    store?.setItem(KEY, value);
  } catch {
    // Full or read-only storage, which lands in the same place as no storage at all.
  }
}

export function readRequestedAccount(store: Storage | null = defaultStore()): string | null {
  try {
    return store?.getItem(KEY) || null;
  } catch {
    return null;
  }
}

export function forgetRequestedAccount(store: Storage | null = defaultStore()): void {
  try {
    store?.removeItem(KEY);
  } catch {
    // As above.
  }
}

/**
 * Whether two addresses name the same account, for the purpose of deciding a screen.
 *
 * Case-insensitive and whitespace-trimmed, because an address typed into a field and an
 * address minted into a claim differ in exactly those ways for the same person. This is
 * deliberately not on the identity path, where `auth/emailRouting.ts` explains at length
 * why normalising is forbidden: the backend derives a person's id from the unnormalised
 * claim, and lowercasing anything there would split one history in two. Here the answer
 * only chooses which of two screens to render.
 *
 * Null on either side is not a match. That is what makes a browser with nobody remembered
 * ask Entra to name an account rather than assuming the session is the right one.
 */
export function sameAccount(a: string | null, b: string | null): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}
